import { useLoadProjectArea } from "@/components/common/project-provider/project-loading-context";
import { useTreeContext } from "@/components/ui/tree/tree-context";
import { IElementImg } from "@/hooks/use-placeholders";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { selectSheetForSheetCadAlignment } from "@/store/modes/sheet-cad-alignment-mode-selectors";
import { selectSheetForSheetToCloudAlignment } from "@/store/modes/sheet-to-cloud-alignment-mode-selectors";
import { selectShouldUseIntensityData } from "@/store/modes/walk-mode-slice";
import {
  selectActiveArea,
  selectActiveElement,
  selectActiveSheetsIfAvailable,
  selectAreaFor,
  selectListVisibleSheets,
} from "@/store/selections-selectors";
import {
  setActiveArea,
  setActiveElement,
  setActiveSheets,
  setSheetVisibility,
} from "@/store/selections-slice";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { IElementOrthomosaic } from "@/types/ielement-extra-types";
import { assert } from "@faro-lotv/foundation";
import {
  IElement,
  IElementGaussianSplats,
  IElementGenericImgSheet,
  IElementGenericStream,
  IElementImgSheet,
  IElementSection,
  IElementTimeSeriesDataSession,
  isIElementGenericImgSheet,
  isIElementGenericPointCloudStream,
  isIElementImgSheet,
  isIElementSectionDataSession,
  pickClosestInTime,
} from "@faro-lotv/ielement-types";
import {
  PointCloudType,
  newestToOldest,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectIElement,
  selectPointCloudType,
} from "@faro-lotv/project-source";
import { isEqual } from "es-toolkit";
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  type JSX,
} from "react";
import {
  CurrentScene,
  WaypointRange,
  selectActiveElementReference,
  selectCurrentScene,
} from "./mode-selectors";
import {
  CurrentAreaData,
  selectCurrentAreaData,
} from "./select-current-area-data";

type ModeDataContext = {
  /** All the interesting project elements about the current active area */
  currentArea?: CurrentAreaData;

  /** The elements the user want to navigate in the current area based on the current selection */
  currentScene?: CurrentScene;

  /** True if the area is currently loading */
  isLoading: boolean;
};

export const ModeDataContext = createContext<ModeDataContext | undefined>(
  undefined,
);

/** @returns a context that determines based on user input and ui state what data each mode should render */
export function ModeDataProvider({ children }: PropsWithChildren): JSX.Element {
  const activeElement = useAppSelector(selectActiveElement);
  const activeArea = useAppSelector(selectActiveArea);
  const activeCad = useAppSelector(selectActiveCadModel);

  const dispatch = useAppDispatch();

  // avoid re-run selectCurrentAreaData if area for activeElement is not changed
  const area = useAppSelector(selectAreaFor(activeElement));
  const currentArea = useAppSelector(selectCurrentAreaData(area), isEqual);

  // TODO: Remove once the new area navigation ui is implemented
  // https://faro01.atlassian.net/browse/SWEB-5708
  useEffect(() => {
    if (currentArea?.area?.id !== activeArea?.id) {
      dispatch(setActiveArea(currentArea?.area?.id));
    }
  }, [activeArea, currentArea?.area?.id, dispatch]);

  const isLoading = useLoadProjectArea(currentArea?.area?.id);
  const activeSheets = useAppSelector(selectActiveSheetsIfAvailable);

  const { tree } = useTreeContext();

  const shouldUseIntensityData = useAppSelector(selectShouldUseIntensityData);
  const currentScene = useAppSelector(
    selectCurrentScene(
      activeElement,
      activeCad,
      currentArea,
      tree,
      shouldUseIntensityData,
    ),
    isEqual,
  );

  // Test if the new list of active sheets is the same as the current scene.
  // true if the contents of `activeSheets` is different from `currentScene?.activeSheets
  // Assume the list are consistent so we can avoid a nested loop.
  const activeSheetsChanged = useMemo(() => {
    if (activeSheets.length !== currentScene?.activeSheets.length) {
      return true;
    }
    // assume the order of the sheets will be consistent so we can avoid a nested loop
    return currentScene.activeSheets.some(
      (sheet, index) => sheet.id !== activeSheets[index]?.id,
    );
  }, [currentScene?.activeSheets, activeSheets]);

  const newActiveSheets = useMemo(() => {
    const newActiveSheets =
      currentScene?.activeSheets.map((sheet) => sheet.id) ?? [];
    if (!newActiveSheets.length && currentScene?.availableSheets.length) {
      newActiveSheets.push(currentScene.availableSheets[0].id);
    }
    return newActiveSheets;
  }, [currentScene?.activeSheets, currentScene?.availableSheets]);

  const listVisibleSheets = useAppSelector(selectListVisibleSheets);

  const firstDisplayedSheet = newActiveSheets[0];
  // The sheet that must be set visible, or undefined
  const sheetToBeMadeVisible = useMemo(
    () =>
      listVisibleSheets.includes(firstDisplayedSheet)
        ? undefined
        : firstDisplayedSheet,
    [firstDisplayedSheet, listVisibleSheets],
  );

  useEffect(() => {
    if (activeSheetsChanged) {
      dispatch(setActiveSheets(newActiveSheets));
    }
    if (sheetToBeMadeVisible !== undefined) {
      // make sure at least one sheet stays visible
      dispatch(
        setSheetVisibility({
          sheetId: sheetToBeMadeVisible,
          isVisible: true,
        }),
      );
    }
  }, [
    activeSheets,
    currentScene?.availableSheets,
    currentScene?.activeSheets,
    dispatch,
    activeSheetsChanged,
    newActiveSheets,
    sheetToBeMadeVisible,
  ]);

  const value = useMemo<ModeDataContext>(
    () => ({
      currentArea,
      currentScene,
      isLoading,
    }),
    [currentArea, currentScene, isLoading],
  );

  return <ModeDataContext value={value}>{children}</ModeDataContext>;
}

/**
 * @returns The data we want to show to the user based on the current user selection if available
 */
export function useCurrentSceneIfAvailable(): CurrentScene | undefined {
  const modeData = useContext(ModeDataContext);
  assert(
    modeData,
    "useCurrentSceneIfAvailable need to be used inside a ModeDataProvider",
  );
  return modeData.currentScene;
}

/**
 * @returns The data we want to show to the user based on the current user selection
 */
export function useCurrentScene(): CurrentScene {
  const currentScene = useCurrentSceneIfAvailable();
  assert(currentScene, "useCurrentScene requires a scene to be available");
  return currentScene;
}

/**
 * @returns The important project data filtered for the user selected area
 */
export function useCurrentAreaIfAvailable(): CurrentAreaData | undefined {
  const modeData = useContext(ModeDataContext);
  assert(modeData, "useCurrentArea need to be used inside a ModeDataProvider");
  return modeData.currentArea;
}

/**
 * @returns The important project data filtered for the user selected area
 */
export function useCurrentArea(): CurrentAreaData {
  const currentArea = useCurrentAreaIfAvailable();
  assert(
    currentArea,
    "useCurrentArea requires at least an area in the project",
  );
  return currentArea;
}

/** @returns true if the current area is loading */
export function useIsCurrentAreaLoading(): boolean {
  return !!useContext(ModeDataContext)?.isLoading;
}

/**
 * @returns The highest and lowest placeholder altitude in the current scene
 */
export function useWaypointAltitudeRange(): WaypointRange | undefined {
  return useCurrentSceneIfAvailable()?.waypointAltitudeRange;
}

/** @returns the range containing all capture dates of all panos in the current scene */
export function useWaypointDateRange(): WaypointRange | undefined {
  return useCurrentSceneIfAvailable()?.waypointDateRange;
}

type SheetAlignmentType = "sheetAndCad" | "sheetToCloud";
/**
 * @param alignType the sheet alignment type
 * @returns The sheet used for in current session of alignment
 */
export function useSheetSelectedForAlignment(
  alignType: SheetAlignmentType,
): IElementGenericImgSheet {
  const sheetId = useAppSelector(
    alignType === "sheetAndCad"
      ? selectSheetForSheetCadAlignment
      : selectSheetForSheetToCloudAlignment,
  );
  if (!sheetId) throw new Error("Current sheet for alignment not specified.");

  const sheet = useAppSelector(selectIElement(sheetId));

  if (!sheet || !isIElementGenericImgSheet(sheet)) {
    throw new Error("Current sheet is not a valid IElementGenericImgSheet.");
  }

  return sheet;
}

/**
 * @returns The list of all ImgSheets in a given time series data session
 * @param timeSeriesDataSession The IElement of type TimeSeries and type hint DataSession in which the ImgSheets are located
 */
export function selectAllImgSheetsInTimeSeriesDataSession(
  timeSeriesDataSession: IElementTimeSeriesDataSession | undefined,
) {
  return (state: RootState): IElementImgSheet[] => {
    const allImgSheetsFound: IElementImgSheet[] = [];
    if (timeSeriesDataSession) {
      const sectionDataSessions = selectChildrenDepthFirst(
        timeSeriesDataSession,
        isIElementSectionDataSession,
      )(state);
      for (const sectionDataSession of sectionDataSessions) {
        const imgSheet = selectChildDepthFirst(
          sectionDataSession,
          (el): el is IElementImgSheet => isIElementImgSheet(el),
        )(state);

        if (imgSheet) {
          allImgSheetsFound.push(imgSheet);
        }
      }
    }
    return allImgSheetsFound;
  };
}

/**
 * Compute a valid model to render from a specific set of sessions
 *
 * @param validSessions the valid sessions
 * @param validModelGuard a guard to identify a valid model to render
 * @param currentModel the current active model
 * @param referenceElement the element identifying the current active time point
 * @returns the valid model to render in the valid sessions if one exists
 */
function selectValidModel<ValidModel extends IElement>(
  validSessions: IElementSection[],
  validModelGuard: (el: IElement) => el is ValidModel,
  currentModel?:
    | IElementImg
    | IElementGenericStream
    | IElementGaussianSplats
    | IElementOrthomosaic,
  referenceElement?: IElementSection,
) {
  return (state: RootState) => {
    if (currentModel && validModelGuard(currentModel)) {
      return currentModel;
    }
    // Search in all the valid sessions for the current mode
    const sortedSessions = validSessions.sort(newestToOldest);

    let session: IElementSection | undefined;
    // If there's a reference element search the closest in time valid session for this mode
    if (referenceElement) {
      const closestInTime = pickClosestInTime(referenceElement, sortedSessions);
      if (closestInTime) {
        session = closestInTime;
      }
    } else if (sortedSessions.length) {
      // If there's no reference element pick the newest session available
      session = sortedSessions[0];
    }
    if (!session) return;

    // Find the model to render in the session
    const model = selectChildDepthFirst(session, validModelGuard)(state);

    // When model is a point cloud stream, default to "Mobile" scan for Orbis datasets
    if (model && isIElementGenericPointCloudStream(model)) {
      const type = selectPointCloudType(model)(state);
      if (type === PointCloudType.flash) {
        // found a Orbis flash scan, try to find a mobile scan
        const mobileScan = selectChildDepthFirst(
          session,
          (el: IElement) =>
            validModelGuard(el) &&
            isIElementGenericPointCloudStream(el) &&
            selectPointCloudType(el)(state) === PointCloudType.mobile,
        )(state);
        if (mobileScan && validModelGuard(mobileScan)) {
          return mobileScan;
        }
      }
    }
    return model;
  };
}

/**
 * Check if the current selection makes sense for the current mode. If this is the case,
 * updates it to the best possible selection.
 * The current selection makes "sense" if either item in `validSessions` or the activeCad
 * is valid according to `validModelGuard` (i.e. validModelGuard returns true) .
 *
 * @param validSessions list of valid data sessions to render in this mode
 * @param validModelGuard a guard to verify if the current model to render is valid for this mode
 * @param fallback what to do if there's no valid session to use in this mode
 * @returns the main model to render in this mode based on the current selection, or undefined if the current selection needs be adjusted
 */
export function useComputeValidModeElement<ValidModel extends IElement>(
  validSessions: IElementSection[],
  validModelGuard: (el: IElement) => el is ValidModel,
  fallback?: () => void,
): ValidModel | undefined {
  const dispatch = useAppDispatch();
  const area = useCurrentArea();
  const { activeElement, referenceElement, main, cad } = useCurrentScene();

  const validModel = useAppSelector(
    selectValidModel(validSessions, validModelGuard, main, referenceElement),
  );
  const validReference = useAppSelector(
    selectActiveElementReference(validModel, area),
  );

  const validCad = cad && validModelGuard(cad) ? cad : undefined;

  // Update the selected element if the current selection is not valid for the calling mode
  useEffect(() => {
    // The active mode is valid = nothing to do
    if (validModel === main && validReference === activeElement) return;

    if (validModel) {
      if (validModel.id !== activeElement.id) {
        // the active element has changed
        dispatch(setActiveElement(validModel.id));
      }
      return;
    }

    // if the model is not valid, but there is an active cad, we are ok
    if (validCad) return;

    fallback?.();
  }, [
    activeElement,
    dispatch,
    fallback,
    main,
    validModel,
    validReference,
    validCad,
  ]);

  return validModel ?? validCad;
}
